import React, { useEffect, useRef, useState } from 'react';
import {
  checkAddressServiceability,
  loadServiceArea,
  serviceAreaRestriction,
} from '../utils/addressServiceability';
import { getDisplayAddressFromGeocodeResults } from '../utils/addressFormatting';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';
import { getAddressSearchPolicy, loadAddressSearchPolicy, subscribeAddressSearchPolicy,
  searchCharacterCount, searchMinimumMessage } from '../utils/addressSearchPolicy';

const DEFAULT_BIAS = {
  lat: 19.059984,
  lng: 72.889999,
};

const DEFAULT_RADIUS_METERS = 50000;

const toNumber = (value, fallback = 0) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : fallback;
};

const normalizePrediction = (prediction, index) => ({
  id: prediction.place_id || `${prediction.description}-${index}`,
  placeId: prediction.place_id,
  description: prediction.description || '',
  distanceMeters: Number.isFinite(prediction.distance_meters)
    ? prediction.distance_meters
    : null,
});

const sortPredictions = (predictions) => [...predictions];

const extractLatLng = (location) => ({
  lat:
    typeof location?.lat === 'function'
      ? location.lat()
      : toNumber(location?.lat),
  lng:
    typeof location?.lng === 'function'
      ? location.lng()
      : toNumber(location?.lng),
});

const NearbyPlacesAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  id,
  required = false,
  disabled = false,
  debounceMs = 250,
  radius = DEFAULT_RADIUS_METERS,
  country = 'in',
  biasLocation,
  className = '',
  containerClassName = 'relative w-full',
  dropdownClassName = 'absolute autocomplete-dropdown-container mt-1 w-full shadow-lg z-10',
  suggestionClassName = 'suggestion-item px-3 py-3 cursor-pointer hover:bg-gray-50',
  activeSuggestionClassName = 'suggestion-item--active px-3 py-3 cursor-pointer bg-gray-100',
  loadingClassName = 'w-full px-3 py-3 bg-white rounded-lg',
  rightAdornment = null,
  multiline = false,
  rows = 3,
  autoComplete = 'new-password',
  name,
  onServiceabilityRejected,
  onServiceabilityError,
}) => {
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const selectedValueRef = useRef('');
  const selectionIdRef = useRef(0);
  const pendingSelectionRef = useRef(null);
  const mountedRef = useRef(false);
  const fieldNameRef = useRef(
    name || `${id || 'place-search'}-${Math.random().toString(36).slice(2)}`
  );

  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [serviceArea, setServiceArea] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [minLength, setMinLength] = useState(getAddressSearchPolicy().minCharacters);
  const [policyReady, setPolicyReady] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const tooShort = !disabled && searchCharacterCount(value) > 0 && searchCharacterCount(value) < minLength;

  useEffect(() => subscribeAddressSearchPolicy(policy => {
    setMinLength(policy.minCharacters);
    setPolicyReady(true);
    setPolicyError('');
  }), []);
  useEffect(() => {
    let active = true;
    loadAddressSearchPolicy().then(policy => {
      if (!active) return;
      setMinLength(policy.minCharacters);
      setPolicyReady(true);
      setPolicyError('');
    }).catch(() => {
      if (!active) return;
      setPolicyReady(false);
      setPolicyError('Address search settings are unavailable. Please try again.');
    });
    return () => { active = false; };
  }, [value, isFocused]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; selectionIdRef.current += 1; };
  }, []);
  useEffect(() => {
    selectionIdRef.current += 1;
    pendingSelectionRef.current = null;
  }, [value, disabled]);

  const initializeMapsServices = () => {
    if (!window.google?.maps?.__instaportBackendServices) {
      return false;
    }

    autocompleteServiceRef.current =
      autocompleteServiceRef.current ||
      new window.google.maps.places.AutocompleteService();
    geocoderRef.current =
      geocoderRef.current || new window.google.maps.Geocoder();

    return true;
  };

  const biasLat = toNumber(biasLocation?.lat, serviceArea?.latitude ?? DEFAULT_BIAS.lat);
  const biasLng = toNumber(biasLocation?.lng, serviceArea?.longitude ?? DEFAULT_BIAS.lng);

  useEffect(() => {
    let active = true;
    loadGoogleMaps().then(() => {
      if (active) setMapsReady(initializeMapsServices());
    }).catch(() => {
      if (active) setSearchError('Address search is unavailable. Please reload and try again.');
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    loadServiceArea().then((area) => {
      if (active) setServiceArea(area);
    }).catch(() => {
      // The final selected coordinate is always verified by the backend.
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current;
    const query = String(value || '').trim();

    if (
      disabled ||
      !isFocused ||
      searchCharacterCount(query) < minLength ||
      !policyReady ||
      !mapsReady
    ) {
      setLoading(false);
      setSuggestions([]);
      setActiveIndex(-1);
      return undefined;
    }

    if (selectedValueRef.current && query === selectedValueRef.current) {
      selectedValueRef.current = '';
      setLoading(false);
      setSuggestions([]);
      setActiveIndex(-1);
      return undefined;
    }

    setSearchError('');
    setLoading(true);

    debounceRef.current = window.setTimeout(() => {
      const maps = window.google?.maps;
      const autocompleteService = autocompleteServiceRef.current;

      if (!maps || !autocompleteService) {
        setLoading(false);
        return;
      }

      const locationBias = new maps.LatLng(biasLat, biasLng);

      autocompleteService.getPlacePredictions(
        {
          input: query,
          componentRestrictions: country ? { country } : undefined,
          location: locationBias,
          origin: locationBias,
          radius,
          ...(serviceArea ? { locationRestriction: serviceAreaRestriction(serviceArea) } : {}),
        },
        (predictions, status) => {
          if (requestIdRef.current !== currentRequestId) {
            return;
          }

          setLoading(false);

          const placesStatus = maps.places?.PlacesServiceStatus;
          if (
            status !== placesStatus?.OK ||
            !Array.isArray(predictions) ||
            predictions.length === 0
          ) {
            setSuggestions([]);
            setActiveIndex(-1);
            if (status !== 'ZERO_RESULTS' && status !== 'OK') {
              setSearchError('Unable to load addresses. Please try again.');
            }
            return;
          }

          const sortedPredictions = sortPredictions(
            predictions.map(normalizePrediction)
          );

          setSuggestions(sortedPredictions);
          setActiveIndex(sortedPredictions.length ? 0 : -1);
        }
      );
    }, debounceMs);

    return () => {
      requestIdRef.current += 1;
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [
    biasLat,
    biasLng,
    country,
    debounceMs,
    disabled,
    isFocused,
    minLength,
    policyReady,
    mapsReady,
    radius,
    serviceArea,
    value,
  ]);

  const clearSuggestions = () => {
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const cancelPendingAutocomplete = () => {
    requestIdRef.current += 1;
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLoading(false);
  };

  const handleSuggestionSelect = async (suggestion) => {
    if (!suggestion?.placeId || !geocoderRef.current || disabled ||
        pendingSelectionRef.current === suggestion.placeId) {
      return;
    }

    const selectionId = ++selectionIdRef.current;
    pendingSelectionRef.current = suggestion.placeId;
    const isCurrent = () => mountedRef.current && selectionIdRef.current === selectionId;

    cancelPendingAutocomplete();
    clearSuggestions();

    geocoderRef.current.geocode({ placeId: suggestion.placeId }, async (results, status) => {
      if (!isCurrent()) return;
      if (status !== 'OK' || !Array.isArray(results) || !results[0]?.geometry?.location) {
        pendingSelectionRef.current = null;
        return;
      }

      const firstResult = results[0];
      const latLng = extractLatLng(firstResult.geometry.location);
      const displayText = suggestion.description;
      // Keep the chosen POI name: geocoding can return only the nearby street.
      const fullAddress = String(displayText || '').trim() ||
        getDisplayAddressFromGeocodeResults(results, displayText);

      const nextSelection = {
        description: displayText,
        address: fullAddress,
        latitude: latLng.lat,
        longitude: latLng.lng,
        placeId: suggestion.placeId,
        distanceMeters: suggestion.distanceMeters,
      };

      setLoading(true);
      try {
        const verification = await checkAddressServiceability(nextSelection);
        if (!isCurrent()) return;
        if (!verification.serviceable) {
          onServiceabilityRejected?.(verification);
          return;
        }
      } catch (error) {
        if (isCurrent()) onServiceabilityError?.(error);
        return;
      } finally {
        if (isCurrent()) {
          pendingSelectionRef.current = null;
          setLoading(false);
        }
      }

      if (!isCurrent()) return;
      const selectionAccepted = onSelect ? onSelect(nextSelection) !== false : true;
      if (selectionAccepted) {
        selectedValueRef.current = displayText;
        if (!onSelect) {
          onChange?.(displayText);
        }
      }
    });
  };

  const handleKeyDown = (event) => {
    if (!suggestions.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((currentIndex) =>
        currentIndex >= suggestions.length - 1 ? 0 : currentIndex + 1
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((currentIndex) =>
        currentIndex <= 0 ? suggestions.length - 1 : currentIndex - 1
      );
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      handleSuggestionSelect(suggestions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      clearSuggestions();
    }
  };

  const Field = multiline ? 'textarea' : 'input';

  return (
    <div className={containerClassName}>
      <div className="relative w-full">
      <Field
        id={id}
        name={fieldNameRef.current}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        inputMode="search"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-protonpass-ignore="true"
        rows={multiline ? rows : undefined}
        value={value}
        aria-invalid={tooShort || undefined}
        aria-describedby={tooShort ? `${fieldNameRef.current}-minimum` : undefined}
        onChange={(event) => {
          selectionIdRef.current += 1;
          pendingSelectionRef.current = null;
          onChange?.(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          window.setTimeout(() => {
            clearSuggestions();
          }, 100);
        }}
        placeholder={placeholder}
        className={className}
      />
      {rightAdornment}
      </div>
      {tooShort && <div role="alert" id={`${fieldNameRef.current}-minimum`}
        style={{ backgroundColor: '#f44336', color: '#fff', padding: '8px 12px', borderRadius: '0 0 5px 5px', fontSize: '14px' }}>
        {searchMinimumMessage(minLength)}
      </div>}
      {isFocused && !tooShort && (policyError || searchError) && <div role="alert" className={loadingClassName}>{policyError || searchError}</div>}
      {!tooShort && (loading || suggestions.length > 0) && (
        <div className={dropdownClassName}>
          {loading && <div className={loadingClassName}>Loading...</div>}
          {!loading &&
            suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onMouseEnter={() => {
                  setActiveIndex(index);
                }}
                onClick={() => {
                  handleSuggestionSelect(suggestion);
                }}
                className={
                  index === activeIndex
                    ? activeSuggestionClassName
                    : suggestionClassName
                }
              >
                <span>{suggestion.description}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default NearbyPlacesAutocomplete;
